<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue';
import { ref, onMounted, computed, nextTick } from 'vue'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableEmptyState } from '@/components/common/table'
import PriceCategoryService from '@/services/PriceCategoryService'
import type { PriceCategory, CreatePriceCategoryRequest, UpdatePriceCategoryRequest } from '@/services/PriceCategoryService'

// Component state
const loading = ref(false)
const error = ref<string | null>(null)
const showModal = ref(false)
const editMode = ref(false)
const isSubmitting = ref(false)

// Data
const priceCategories = ref<PriceCategory[]>([])
const newSubcategory = ref('')
const editingCategoryId = ref<number | null>(null)

// Subcategory editing state
const editingSubcategoryIndex = ref<number | null>(null)
const editingSubcategoryValue = ref('')

// Form data
const priceCategoryForm = ref<CreatePriceCategoryRequest>({
    name: '',
    description: '',
    subcategories: [],
    is_active: true,
    sort_order: 0
})

// Computed properties
const priceCategoriesWithDetails = computed(() => {
    return priceCategories.value.map(category => ({
        id: category.id,
        name: category.name,
        description: category.description,
        priceCount: category.price_count,
        subcategories: category.subcategories || [],
        is_active: category.is_active,
        sort_order: category.sort_order
    })).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
})

// Load data on mount
onMounted(() => {
    fetchPriceCategories()
})

// Fetch all price categories
async function fetchPriceCategories() {
    loading.value = true
    error.value = null

    try {
        const response = await PriceCategoryService.getAllCategories()
        const data = response.data.data || response.data
        priceCategories.value = Array.isArray(data) ? data : []
    } catch (err) {
        console.error('Error loading price categories:', err)
        error.value = 'Nepodarilo sa načítať kategórie cien. Skúste to znovu.'
    } finally {
        loading.value = false
    }
}

// Modal handlers
function handleAddPriceCategory() {
    editMode.value = false
    editingCategoryId.value = null
    priceCategoryForm.value = {
        name: '',
        description: '',
        subcategories: [],
        is_active: true,
        sort_order: 0
    }
    newSubcategory.value = ''
    // Cancel any ongoing subcategory editing
    cancelSubcategoryEdit()
    showModal.value = true
}

function handleEditPriceCategory(priceCategory: { id: number; name: string; description?: string; subcategories: string[]; is_active: boolean; sort_order: number }) {
    editMode.value = true
    editingCategoryId.value = priceCategory.id
    priceCategoryForm.value = {
        name: priceCategory.name,
        description: priceCategory.description || '',
        subcategories: [...(priceCategory.subcategories || [])],
        is_active: priceCategory.is_active,
        sort_order: priceCategory.sort_order
    }
    newSubcategory.value = ''
    // Cancel any ongoing subcategory editing
    cancelSubcategoryEdit()
    showModal.value = true
}

function closeModal() {
    showModal.value = false
    priceCategoryForm.value = {
        name: '',
        description: '',
        subcategories: [],
        is_active: true,
        sort_order: 0
    }
    newSubcategory.value = ''
    editingCategoryId.value = null
    error.value = null
    // Cancel any ongoing subcategory editing
    cancelSubcategoryEdit()
}

// Subcategory management
function addSubcategory() {
    if (newSubcategory.value.trim() && !priceCategoryForm.value.subcategories?.includes(newSubcategory.value.trim())) {
        if (!priceCategoryForm.value.subcategories) {
            priceCategoryForm.value.subcategories = []
        }
        priceCategoryForm.value.subcategories.push(newSubcategory.value.trim())
        newSubcategory.value = ''
    }
}

function removeSubcategory(index: number) {
    if (priceCategoryForm.value.subcategories) {
        priceCategoryForm.value.subcategories.splice(index, 1)
    }
}

// Subcategory editing
async function startEditSubcategory(index: number, subcategory: string) {
    editingSubcategoryIndex.value = index
    editingSubcategoryValue.value = subcategory

    // Focus the input field after Vue updates the DOM
    await nextTick()
}

function saveSubcategoryEdit(index: number) {
    if (editingSubcategoryValue.value.trim() && priceCategoryForm.value.subcategories) {
        // Check for duplicates (excluding the current one being edited)
        const trimmedValue = editingSubcategoryValue.value.trim()
        const isDuplicate = priceCategoryForm.value.subcategories.some((sub, i) =>
            i !== index && sub === trimmedValue
        )

        if (!isDuplicate) {
            priceCategoryForm.value.subcategories[index] = trimmedValue
            cancelSubcategoryEdit()
        } else {
            // Could show a toast or error message here
            alert('Podkategória s týmto názvom už existuje')
        }
    }
}

function cancelSubcategoryEdit() {
    editingSubcategoryIndex.value = null
    editingSubcategoryValue.value = ''
}

// Form submission
async function handleSubmit() {
    if (!priceCategoryForm.value.name?.trim()) return

    isSubmitting.value = true
    error.value = null

    try {
        if (editMode.value && editingCategoryId.value) {
            // Update existing category
            const updateData: UpdatePriceCategoryRequest = {
                name: priceCategoryForm.value.name,
                description: priceCategoryForm.value.description,
                subcategories: priceCategoryForm.value.subcategories,
                is_active: priceCategoryForm.value.is_active,
                sort_order: priceCategoryForm.value.sort_order
            }
            await PriceCategoryService.updateCategory(editingCategoryId.value, updateData)
        } else {
            // Create new category
            await PriceCategoryService.createCategory(priceCategoryForm.value)
        }

        await fetchPriceCategories() // Refresh data
        closeModal()
    } catch (err: unknown) {
        console.error('Error saving category:', err)
        if (err && typeof err === 'object' && 'response' in err) {
            const response = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response
            if (response?.data?.message) {
                error.value = response.data.message
            } else if (response?.data?.errors) {
                // Handle validation errors
                const errorMessages: string[] = []
                for (const field in response.data.errors) {
                    errorMessages.push(...response.data.errors[field])
                }
                error.value = errorMessages.join(', ')
            } else {
                error.value = 'Nepodarilo sa uložiť kategóriu. Skúste to znovu.'
            }
        } else {
            error.value = 'Nepodarilo sa uložiť kategóriu. Skúste to znovu.'
        }
    } finally {
        isSubmitting.value = false
    }
}

async function handleDeletePriceCategory(priceCategory: { id: number; name: string; priceCount: number }) {
    if (priceCategory.priceCount > 0) {
        alert('Nie je možné vymazať kategóriu, ktorá obsahuje ceny. Najprv vymaže alebo presuňte všetky ceny.')
        return
    }

    if (confirm(`Naozaj chcete vymazať kategóriu "${priceCategory.name}"?`)) {
        try {
            await PriceCategoryService.deleteCategory(priceCategory.id)
            await fetchPriceCategories() // Refresh data
        } catch (err: unknown) {
            console.error('Error deleting category:', err)
            if (err && typeof err === 'object' && 'response' in err) {
                const response = (err as { response?: { data?: { message?: string } } }).response
                if (response?.data?.message) {
                    error.value = response.data.message
                } else {
                    error.value = 'Nepodarilo sa vymazať kategóriu.'
                }
            } else {
                error.value = 'Nepodarilo sa vymazať kategóriu.'
            }
        }
    }
}
</script>

<template>
    <main class="flex-1">
        <div class="py-6">
            <div class="max-w-(--breakpoint-2xl) mx-auto">
                <div class="flex justify-between items-center">
                    <PageBreadcrumb page-title="Správa kategórií cien" />
                </div>
            </div>
            <TableCard title="Kategórie cien">
                <template #controls>
                    <button
                        type="button"
                        class="shrink-0 px-4 py-2 bg-brand-600 text-white rounded-md"
                        @click="handleAddPriceCategory"
                    >
                        Pridať kategóriu
                    </button>
                </template>
                <div class="max-w-full overflow-x-auto min-w-0">
                    <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                        <TableHeader
                            class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
                        >
                            <TableRow>
                                <TableCell class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Názov</TableCell>
                                <TableCell class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Popis</TableCell>
                                <TableCell class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Ceny</TableCell>
                                <TableCell class="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Aktívne</TableCell>
                                <TableCell class="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Akcie</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                            <TableEmptyState
                                v-if="priceCategoriesWithDetails.length === 0"
                                title="Žiadne kategórie cien"
                                hint="Pridajte prvú kategóriu."
                                colspan="5"
                            />
                            <template v-for="category in priceCategoriesWithDetails" :key="category.id">
                                <TableRow class="hover:bg-gray-50 dark:hover:bg-white/5">
                                    <TableCell class="px-6 py-3 text-left text-sm font-medium text-gray-800 dark:text-white">{{ category.name }}</TableCell>
                                    <TableCell class="px-6 py-3 text-left text-sm text-gray-500 dark:text-gray-400">{{ category.description }}</TableCell>
                                    <TableCell class="px-6 py-3 text-left text-sm text-gray-500 dark:text-gray-400">{{ category.priceCount }}</TableCell>
                                    <TableCell class="px-6 py-3 text-center text-sm">
                                        <span :class="category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                                            {{ category.is_active ? 'Áno' : 'Nie' }}
                                        </span>
                                    </TableCell>
                                    <TableCell class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-white/90">
                                        <button @click="() => handleEditPriceCategory(category)" class="px-2 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400">Upraviť</button>
                                        <button @click="() => handleDeletePriceCategory(category)" class="ml-2 px-2 py-1 text-sm text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400">Vymazať</button>
                                    </TableCell>
                                </TableRow>
                            </template>
                        </TableBody>
                    </Table>
                </div>
            </TableCard>
        </div>
    </main>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/30 flex items-center justify-center z-50 dark:bg-white/3">
        <div class="bg-white p-6 rounded-sm shadow-lg w-full max-w-md dark:bg-white/3">
            <h2 class="text-xl font-bold mb-4">{{ editMode ? 'Upraviť kategóriu' : 'Pridať kategóriu' }}</h2>
            <form @submit.prevent="handleSubmit" class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Názov kategórie</label>
                    <input type="text" v-model="priceCategoryForm.name" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popis</label>
                    <textarea v-model="priceCategoryForm.description" rows="3" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"></textarea>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Podkategórie</label>
                    <div v-if="priceCategoryForm.subcategories" class="mt-1">
                        <div v-for="(sub, index) in priceCategoryForm.subcategories" :key="index" class="flex items-center gap-2 mb-2">
                            <input v-if="editingSubcategoryIndex === index" v-model="editingSubcategoryValue" class="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                            <span v-else>{{ sub }}</span>
                            <button v-if="editingSubcategoryIndex === index" @click="saveSubcategoryEdit(index)" class="text-xs text-green-600 hover:text-green-700 dark:text-green-500">Uložiť</button>
                            <button v-else @click="startEditSubcategory(index, sub)" class="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-500">Upraviť</button>
                            <button @click="removeSubcategory(index)" class="text-xs text-red-600 hover:text-red-700 dark:text-red-500">Vymazať</button>
                        </div>
                    </div>
                    <div class="flex gap-2 mt-2">
                        <input v-model="newSubcategory" @keyup.enter="addSubcategory" type="text" placeholder="Nová podkategória" class="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                        <button @click="addSubcategory" class="px-3 py-1 border border-gray-300 rounded-md px-2 py-1 text-sm hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 dark:text-white">Pridať</button>
                    </div>
                    <p v-if="priceCategoryForm.subcategories?.length === 0" class="text-xs text-gray-500 mt-1 dark:text-gray-400">Žiadne podkategórie</p>
                </div>
                <div class="flex justify-end pt-4">
                    <button type="button" @click="closeModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700">Zrušiť</button>
                    <button type="submit" class="ml-3 px-4 py-2 text-sm font-medium text-white bg-brand-600 border border-transparent rounded-md hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500" :disabled="isSubmitting">{{ isSubmitting ? 'Ukladám...' : 'Uložiť' }}</button>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped>
/* Add any component-specific styles here */
</style>
